import { useState } from "react";
import { X } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";

interface AssignLeadDialogProps {
  open: boolean;
  leadName: string;
  currentAssigneeId?: string;
  onClose: () => void;
  onConfirm: (adminUserId: string) => void;
  loading: boolean;
}

export function AssignLeadDialog({
  open,
  leadName,
  currentAssigneeId,
  onClose,
  onConfirm,
  loading,
}: AssignLeadDialogProps) {
  const { t } = useLocale();
  const [userId, setUserId] = useState(currentAssigneeId ?? "");

  if (!open) return null;

  const canSubmit = userId.trim() !== "" && !loading;

  const handleConfirm = () => {
    if (!canSubmit) return;
    onConfirm(userId.trim());
    setUserId("");
  };

  const handleClose = () => {
    setUserId(currentAssigneeId ?? "");
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={handleClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-card shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <h2 className="text-lg font-semibold text-foreground">
              {t("leads.assign.title")}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="space-y-4 px-6 py-5">
            <p className="text-sm text-muted-foreground">
              {t("leads.assign.description").replace("{name}", leadName)}
            </p>

            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("leads.assign.userId")}
              </label>
              <input
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder={t("leads.assign.userIdPlaceholder")}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t("leads.assign.hint")}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!canSubmit}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? t("common.loading") : t("leads.assign.confirm")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
